import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import {
  type Edge,
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box, Card } from '@mui/material';
import { useResizeObserver } from '@realadvisor/observe';
import { createPortal } from 'react-dom';

import {
  useKanbanSelectedState,
  useKanbanState,
} from '@modules/_shared/components/kanban/KanbanBoard';

type KanbanItemProps<ItemData = any> = {
  data: ItemData;
  itemId: string;
  renderItem: (data: ItemData) => React.ReactNode;
  columnId?: string;
  onClick?: () => void;
  onHeightCalculated: (height: number) => void;
  canDrag?: boolean;
  isDropTarget?: boolean;
  getBorderColor?: (data: ItemData) => string | undefined;
};

const ItemContent = forwardRef<
  HTMLDivElement,
  {
    isDragging?: boolean;
    renderItem: (data: any) => React.ReactNode;
    data: any;
    closestEdge: Edge | null;
    onItemClicked?: () => void;
    isPreview?: boolean;
    canDrag?: boolean;
    itemId: string;
    getBorderColor?: (data: any) => string | undefined;
  }
>(
  (
    {
      isDragging = false,
      renderItem,
      data,
      closestEdge,
      onItemClicked,
      isPreview = false,
      canDrag = true,
      itemId,
      getBorderColor,
    },
    ref,
  ) => {
    const renderedItem = useMemo(() => renderItem(data), [data, renderItem]);
    const { selectedItems } = useKanbanSelectedState();

    return (
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          transform: isPreview ? 'rotate(5deg)' : 'none',
        }}
      >
        <Card
          ref={ref}
          variant="outlined"
          elevation={0}
          onClick={onItemClicked}
          sx={theme => ({
            my: 0.5,
            transition: 'all 0.3s ease',
            cursor: canDrag
              ? 'grab'
              : onItemClicked != null
              ? 'pointer'
              : 'default',
            opacity: isDragging ? 0.4 : 1,
            borderColor: selectedItems?.includes(itemId)
              ? theme.palette.primary.main
              : getBorderColor?.(data) ?? 'grey.200',
            boxShadow: selectedItems?.includes(itemId)
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : 'none',
            borderRadius: '10px',
            width: '100%',
          })}
        >
          {renderedItem}
        </Card>
        {closestEdge && <DropIndicator edge={closestEdge} gap="6px" />}
      </Box>
    );
  },
);

type State =
  | { type: 'idle' }
  | { type: 'preview'; container: HTMLElement; rect: DOMRect }
  | { type: 'dragging' };

const idleState: State = { type: 'idle' };
const draggingState: State = { type: 'dragging' };

export const KanbanItem = memo(
  ({
    renderItem,
    data,
    itemId,
    columnId,
    onClick,
    onHeightCalculated,
    canDrag = true,
    isDropTarget = true,
    getBorderColor,
  }: KanbanItemProps) => {
    const { disabled, instanceId } = useKanbanState();
    const ref = useRef<HTMLDivElement | null>(null);
    const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
    const [state, setState] = useState<State>(idleState);

    useEffect(() => {
      const element = ref.current;

      if (element == null) {
        return;
      }

      return combine(
        draggable({
          element,
          getInitialData: () => ({
            content: data,
            type: 'card',
            itemId,
            instanceId,
            columnId,
          }),
          canDrag: () => canDrag,
          onGenerateDragPreview: ({ location, source, nativeSetDragImage }) => {
            const rect = source.element.getBoundingClientRect();

            setCustomNativeDragPreview({
              nativeSetDragImage,
              getOffset: () => {
                // I don't know why but the helper preserveOffsetOnSource() is not working, so we are using this workaround.
                return {
                  x: location.current.input.clientX - rect.x,
                  y: location.current.input.clientY - rect.y,
                };
              },
              render({ container }) {
                setState({ type: 'preview', container, rect });

                return () => setState(draggingState);
              },
            });
          },
          onDragStart: () => setState(draggingState),
          onDrop: () => setState(idleState),
        }),
        dropTargetForExternal({
          element,
          canDrop: () => isDropTarget,
        }),
        dropTargetForElements({
          element,
          canDrop: ({ source }) => {
            if (!isDropTarget) {
              return false;
            }

            return (
              source.data.instanceId === instanceId &&
              source.data.type === 'card' &&
              !disabled
            );
          },
          getIsSticky: () => true,
          getData: ({ input, element }) =>
            attachClosestEdge(
              { content: data, type: 'card', itemId, columnId },
              {
                input,
                element,
                allowedEdges: ['top', 'bottom'],
              },
            ),
          onDragEnter: args => {
            if (args.source.data.itemId !== itemId) {
              setClosestEdge(extractClosestEdge(args.self.data));
            }
          },
          onDrag: args => {
            if (args.source.data.itemId !== itemId) {
              setClosestEdge(extractClosestEdge(args.self.data));
            }
          },
          onDragLeave: () => {
            setClosestEdge(null);
          },
          onDrop: () => {
            setClosestEdge(null);
          },
        }),
      );
    }, [instanceId, itemId, columnId, data, disabled, canDrag, isDropTarget]);

    useResizeObserver(ref, () => {
      const element = ref.current;
      if (element) {
        onHeightCalculated(element.getBoundingClientRect().height + 4);
      }
    });

    return (
      <>
        <ItemContent
          itemId={itemId}
          isDragging={state.type === 'dragging'}
          renderItem={renderItem}
          data={data}
          closestEdge={closestEdge}
          ref={ref}
          onItemClicked={onClick}
          canDrag={canDrag}
          getBorderColor={getBorderColor}
        />
        {state.type === 'preview' &&
          createPortal(
            <Box
              sx={{
                /**
                 * Ensuring the preview has the same dimensions as the original.
                 *
                 * Using `border-box` sizing here is not necessary in this
                 * specific example, but it is safer to include generally.
                 */
                boxSizing: 'border-box',
                width: `${state.rect.width}px`,
                height: `${state.rect.height}px`,
              }}
            >
              <ItemContent
                itemId={itemId}
                renderItem={renderItem}
                data={data}
                closestEdge={null}
                isPreview
                getBorderColor={getBorderColor}
              />
            </Box>,
            state.container,
          )}
      </>
    );
  },
);
